import angular, { IPromise } from 'angular'
import { StoreValue } from 'idb'
import { $q } from '../../ng-compat/imports'
import {
  get as getCustomer,
  list as listCustomers,
} from '../../components/customer/customers-resource/customers-resource'
import { getFeatureFromCache } from '../../util/feature-cache'
import { EntityStore, SyncData, VersionData } from './entity-store'
import { WebRegisterDb } from './database-types'
import { versionsStore } from './versions-store'

type StoreName = 'customers'

/**
 * Stores information on the retailer's customers.
 *
 * @class CustomersStore
 * @constructor
 * @extends EntityStore
 */
export class CustomersStore extends EntityStore<'customers'> {
  private readonly versionsStore = versionsStore

  constructor() {
    super({
      entityStoreName: 'customers',
    })
  }

  /**
   * Get the entities from IndexedDB store or API.
   *
   * @return A promise resolved with the array of all entities
   */
  public all() {
    if (this.isDbStoreDisabled()) {
      return listCustomers()
    }
    return super.all()
  }

  /**
   * Get customer information from the API or IndexedDB
   *
   * @param id
   *        The ID of the customer to retrieve from the database
   *
   * @return A promise resolved with the requested customer's details
   */
  public get(id: string) {
    if (this.isDbStoreDisabled()) {
      return getCustomer(id)
    }
    return super.get(id, true)
  }

  /**
   * Get the information if a entity should be persisted or not
   *
   * @return {boolean} true or false
   */
  public shouldBeSynchronized(): boolean {
    return !this.isDbStoreDisabled()
  }

  /**
   * Sync the given entity data with the IndexedDB store, if `register_online_only_customers_webregister` is true.
   *
   * @deprecated Use `syncDataInNativePromise` instead
   *
   * @param entities
   *        Array of entity objects as returned by the relevant API end point
   * @param [versionData]
   *        Optional information on the sync versions contained within the payload and of the relevant entity.
   *        @note This should only be set when performing a versioned sync of this entity.
   *
   *        @param [versionData.lastSyncedVersion]
   *               The last synced version of the relevant entity prior to this payload
   *
   *        @param [versionData.payloadVersion]
   *               Object containing the `min` and `max` version represented in the given payload
   *
   * @return A promise resolved once the given entity data has been synced with the IndexedDB store
   */
  public syncData(
    entities: Array<StoreValue<WebRegisterDb, StoreName>>,
    versionData?: VersionData
  ): IPromise<SyncData<StoreName>> {
    return $q.when(this.syncDataInNativePromise(entities, versionData))
  }

  /**
   * Sync the given entity data with the IndexedDB store, if `register_online_only_customers_webregister` is true.
   * This returns native promise so you should use wrapp the method in $q when called in Angular code.
   */
  public syncDataInNativePromise(
    entities: Array<StoreValue<WebRegisterDb, StoreName>>,
    versionData?: VersionData
  ): Promise<SyncData<StoreName>> {
    if (this.isDbStoreDisabled()) {
      return Promise.resolve({ updated: [], removed: [] })
    }

    return super.syncDataInNativePromise(entities, versionData)
  }

  /**
   * Executes any processing that needs to be done after initializing the entity
   *
   * @return {IPromise<void> | IPromise<[void, any]>} IPromise<void> | IPromise<[void, any]>
   */
  public postInitProcess() {
    const shouldCleanCustomersStore = getFeatureFromCache(
      'register_remove_local_customer_data_webregister'
    )
    if (shouldCleanCustomersStore && this.isDbStoreDisabled()) {
      return $q.all([
        this.dbStore.clear(),
        this.versionsStore.removeEntityVersion('customers'),
      ])
    }
    return super.postInitProcess()
  }

  protected isDbStoreDisabled(): boolean {
    return getFeatureFromCache('register_online_only_customers_webregister')
  }
}

export const customersStore = new CustomersStore()

/**
 * Responsible for persisting storage of customers from the /api/2.0/customers API end point into IndexedDB.
 *
 * @module customersStore
 */
angular
  .module('webregister.factories')
  .factory('customersStore', () => customersStore)
